/**
 * System-only badge
 *
 * Marks an option that only system-level credentials can see. Hovering or focusing the icon shows
 * the explanation, and the same text is the icon's accessible name. The badge always follows the
 * text it marks. Inside a control (a button, link, tab or menu entry) the icon goes after the label:
 * hovering the icon or giving the control keyboard focus shows the explanation, and the explanation
 * describes the control. SystemOnlyGroup outlines a run of controls and carries the badge once.
 */

import React, {
  createContext,
  RefObject,
  useContext,
  useId,
  useRef,
  useState
} from 'react';
import { Popup } from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import { UserLock } from 'lucide-react';
import Styles from './SystemOnlyBadge.module.css';

export const systemOnlyBadgeKeys = {
  TITLE: 'general.system-only.title',
  BODY: 'general.system-only.body'
};

const SystemOnlyBadge: React.FC = () => {
  const title = I18n.t(systemOnlyBadgeKeys.TITLE);
  const body = I18n.t(systemOnlyBadgeKeys.BODY);

  return (
    <Popup
      on={['hover', 'focus']}
      position="top center"
      header={title}
      content={body}
      trigger={
        <span
          className={Styles.badge}
          role="img"
          tabIndex={0}
          aria-label={`${title}. ${body}`}
        >
          <UserLock aria-hidden="true" size={16} />
        </span>
      }
    />
  );
};

const SystemOnlyPopupContext = createContext<{
  iconRef: RefObject<HTMLSpanElement>;
  setIconHovered: (hovered: boolean) => void;
} | null>(null);

/** Focus that shows a focus ring, which keyboard focus does and a mouse click does not. */
const hasKeyboardFocus = (element: Element) => {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
};

/** The badge's icon alone, for inside a control wrapped in SystemOnlyPopup. */
export const SystemOnlyIcon: React.FC = () => {
  const popup = useContext(SystemOnlyPopupContext);
  return (
    <span
      ref={popup?.iconRef}
      className={Styles.icon}
      onMouseEnter={() => popup?.setIconHovered(true)}
      onMouseLeave={() => popup?.setIconHovered(false)}
    >
      <UserLock aria-hidden="true" size={14} />
    </span>
  );
};

/** Shows the explanation over the control's SystemOnlyIcon, and describes the control with it. */
export const SystemOnlyPopup: React.FC<{ children: React.ReactElement }> = ({
  children
}) => {
  const descriptionId = useId();
  const iconRef = useRef<HTMLSpanElement>(null);
  const [iconHovered, setIconHovered] = useState(false);
  const [keyboardFocused, setKeyboardFocused] = useState(false);
  const title = I18n.t(systemOnlyBadgeKeys.TITLE);
  const body = I18n.t(systemOnlyBadgeKeys.BODY);

  return (
    <SystemOnlyPopupContext.Provider value={{ iconRef, setIconHovered }}>
      {React.cloneElement(children, {
        'aria-describedby': descriptionId,
        onFocus: (event: React.FocusEvent<HTMLElement>) => {
          children.props.onFocus?.(event);
          setKeyboardFocused(hasKeyboardFocus(event.currentTarget));
        },
        onBlur: (event: React.FocusEvent<HTMLElement>) => {
          children.props.onBlur?.(event);
          setKeyboardFocused(false);
        }
      })}
      <Popup
        context={iconRef}
        open={iconHovered || keyboardFocused}
        onClose={() => {
          setIconHovered(false);
          setKeyboardFocused(false);
        }}
        position="top center"
        header={title}
        content={body}
      />
      <span id={descriptionId} hidden>
        {`${title}. ${body}`}
      </span>
    </SystemOnlyPopupContext.Provider>
  );
};

export default SystemOnlyBadge;
